import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ITableMetadata } from '../../common/interfaces/common.interface';
import { SortOrderEnum } from '../../common/enums/common.enum';
import { PAGE_SIZES } from '../../components/BasicTable/BasicTable';

/** Query parameters a list owns. Anything else in the URL (kindergarten, tab) is left alone. */
const SEARCH = 'q';
const PAGE = 'page';
const PER_PAGE = 'perPage';
const SORT = 'sort';
const ORDER = 'order';

export interface ListUrlOptions {
  /** Every list has an order; this is the one it opens with. */
  defaultSort?: { sortBy: string; sortOrder: SortOrderEnum };
  defaultPerPage?: number;
  /** On/off filters and what they are when the URL says nothing, e.g. "only debtors" on. */
  flagDefaults?: Record<string, boolean>;
}

/**
 * A list's search, filters, sort and page, kept in the URL.
 *
 * In the URL rather than in component state so that a reload, a shared link and - the case
 * that matters most - "back" from a detail page all land on the same filtered page the admin
 * left. Writes replace the history entry: stepping through pages is not navigation, and "back"
 * should leave the list rather than walk through every page it showed.
 *
 * Any change to what is shown resets the page, because page 4 of a different filter is
 * rarely a page that exists.
 */
export const useListUrlState = (options: ListUrlOptions = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const defaultPerPage = options.defaultPerPage ?? PAGE_SIZES[0];

  const write = useCallback(
    (change: (next: URLSearchParams) => void, { keepPage = false } = {}) => {
      const next = new URLSearchParams(location.search);
      change(next);
      if (!keepPage) next.delete(PAGE);
      const query = next.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true, state: location.state });
    },
    [location.pathname, location.search, location.state, navigate]
  );

  const positive = (raw: string | null, fallback: number) => {
    const value = Number(raw);
    return Number.isInteger(value) && value > 0 ? value : fallback;
  };

  const search = params.get(SEARCH) ?? '';
  const page = positive(params.get(PAGE), 1);
  const perPage = positive(params.get(PER_PAGE), defaultPerPage);
  const rawOrder = params.get(ORDER);
  const sortBy = params.get(SORT) ?? options.defaultSort?.sortBy;
  const sortOrder =
    rawOrder === SortOrderEnum.ASC || rawOrder === SortOrderEnum.DESC
      ? (rawOrder as SortOrderEnum)
      : params.get(SORT)
        ? SortOrderEnum.ASC
        : options.defaultSort?.sortOrder;

  const values = (key: string): string[] => params.getAll(key);
  const flag = (key: string): boolean => {
    const raw = params.get(key);
    return raw === null ? (options.flagDefaults?.[key] ?? false) : raw === '1';
  };

  const setSearch = (value: string) =>
    write(next => {
      if (value.trim()) next.set(SEARCH, value);
      else next.delete(SEARCH);
    });

  const setValues = (key: string, selected: string[]) =>
    write(next => {
      next.delete(key);
      selected.forEach(value => next.append(key, value));
    });

  const setFlag = (key: string, on: boolean) =>
    write(next => {
      // the default is written as nothing, so a clean URL means "as the list opens"
      if (on === (options.flagDefaults?.[key] ?? false)) next.delete(key);
      else next.set(key, on ? '1' : '0');
    });

  const setSort = (key: string, order: 'asc' | 'desc') =>
    write(next => {
      next.set(SORT, key);
      next.set(ORDER, order);
    });

  const setPage = (value: number) => write(next => next.set(PAGE, String(value)), { keepPage: true });
  const setPerPage = (value: number) =>
    write(next => {
      if (value === defaultPerPage) next.delete(PER_PAGE);
      else next.set(PER_PAGE, String(value));
    });

  /** Back to how the list opens: no search, no filters. The sort and page size stay. */
  const clearFilters = (keys: string[]) =>
    write(next => {
      next.delete(SEARCH);
      keys.forEach(key => next.delete(key));
    });

  /**
   * BasicTable's paging props, driven by the URL. BasicTable hands back a whole metadata
   * object; only the page and its size are ours to keep.
   */
  const tableProps = (totalItems: number) => {
    const metadata: ITableMetadata = {
      currentPage: page,
      itemsPerPage: perPage,
      totalItems,
      totalPages: Math.ceil(totalItems / perPage)
    };
    const setMetadata: React.Dispatch<React.SetStateAction<ITableMetadata>> = action => {
      const next = typeof action === 'function' ? action(metadata) : action;
      if (next.itemsPerPage !== perPage) setPerPage(next.itemsPerPage);
      else if (next.currentPage !== page) setPage(next.currentPage);
    };
    return { metadata, setMetadata };
  };

  return {
    search,
    setSearch,
    values,
    setValues,
    flag,
    setFlag,
    sortBy,
    sortOrder,
    setSort,
    page,
    perPage,
    clearFilters,
    tableProps,
    /** The URL a detail page should come back to, filters and all. */
    here: `${location.pathname}${location.search}`
  };
};
